import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Menu, X, LogOut, LayoutDashboard, List, MessageSquare } from "lucide-react";

export default function Header({ isAdmin }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const publicLinks = [
    { to: "/", label: "Home" },
    { to: "/properties", label: "Properties" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
  ];

  const adminLinks = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard },
    { to: "/admin/listings", label: "Listings", icon: List },
    { to: "/admin/inquiries", label: "Inquiries", icon: MessageSquare },
  ];

  const links = isAdmin ? adminLinks : publicLinks;
  const isActive = (path) => location.pathname === path;

  return (
    <header data-testid="site-header" className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-xl border-b border-[#E2E8F0] shadow-sm">
      <div className="px-6 md:px-12 lg:px-24 flex items-center justify-between h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
          <img src="/logo.png" alt="K10 Properties Group" className="h-[72px] w-auto" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              className={`text-sm uppercase tracking-[0.15em] transition-colors duration-200 ${
                isActive(link.to)
                  ? "text-[#32A334]"
                  : "text-[#64748B] hover:text-[#1A1A2E]"
              }`}
            >
              {isAdmin && link.icon && (
                <link.icon className="inline-block w-4 h-4 mr-2 -mt-0.5" />
              )}
              {link.label}
            </Link>
          ))}
          {isAdmin && user && (
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="text-sm uppercase tracking-[0.15em] text-[#64748B] hover:text-[#D94E4E] transition-colors duration-200 flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          )}
        </nav>

        {/* Mobile Toggle */}
        <button
          className="md:hidden text-[#1A1A2E]"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-xl border-t border-[#E2E8F0] animate-fade-in" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className={`text-sm uppercase tracking-[0.15em] py-2 ${
                  isActive(link.to) ? "text-[#32A334]" : "text-[#64748B]"
                }`}
              >
                {link.label}
              </Link>
            ))}
            {isAdmin && user && (
              <button onClick={handleLogout} className="text-sm uppercase tracking-[0.15em] text-[#64748B] text-left py-2">
                Logout
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
